import React, { FC } from 'react';
import { FindingTypeDetails, FindingTypeTemplateDetails } from '../AnnotationStandardForm.slice';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { fadein, item } from '../../../Shared/Motion';
import classNames from 'classnames';
import styles from './SelectedFindingType.module.scss';
import { PauseOutlined } from '@ant-design/icons';
import { Modal } from 'antd';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  findingTypes: FindingTypeDetails[];
  setFindingTypes: (types: FindingTypeDetails[]) => void;
  selectedFindingTypes: Array<FindingTypeTemplateDetails>;
  onUpdateSelectedFindingTypeTemplates: (selectedTemplates: FindingTypeTemplateDetails[]) => void;
};

const portal: HTMLElement = document.createElement('div');
document.body.appendChild(portal);

export const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const FindingTypesOverview: FC<Props> = ({
  showModal,
  closeModal,
  findingTypes,
  setFindingTypes,
  selectedFindingTypes,
  onUpdateSelectedFindingTypeTemplates
}) => {
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const newOrder: FindingTypeDetails[] = reorder(findingTypes, result.source.index, result.destination.index);
    let orderPopulated = newOrder.map((x, order) => ({
      ...x,
      order
    }));
    orderPopulated = orderPopulated.sort((a, b) => a.order - b.order);

    const newTemplatesOrder = orderPopulated.map(x => {
      return selectedFindingTypes.find(y => y.id === x.findingTypeTemplateId)!;
    });

    setFindingTypes(orderPopulated);
    onUpdateSelectedFindingTypeTemplates(newTemplatesOrder);
  };

  const getCard = (provided: DraggableProvided, snapshot: DraggableStateSnapshot, findingType: FindingTypeDetails) => {
    const card = (
      <div
        className={classNames(styles.orderableCard, {
          [styles.inPortal]: snapshot.isDragging
        })}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
      >
        <span>{findingType.findingTypeTemplate?.name}</span>
        <PauseOutlined rotate={90} />
      </div>
    );

    if (!snapshot.isDragging) {
      return card;
    }

    return ReactDOM.createPortal(card, portal);
  };

  return (
    <Modal visible={showModal} title='Selected Finding types' onCancel={closeModal} footer={false}>
      <motion.div initial='hidden' animate='visible' variants={fadein}>
        <motion.div className={styles.columnsBox}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='list'>
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {findingTypes.map((findingType: FindingTypeDetails, index) => (
                    <motion.div variants={item} key={index}>
                      <Draggable key={findingType.findingTypeTemplateId} draggableId={findingType.findingTypeTemplateId} index={index}>
                        {(provided, snapshot) => getCard(provided, snapshot, findingType)}
                      </Draggable>
                    </motion.div>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </motion.div>
      </motion.div>
    </Modal>
  );
};

export default FindingTypesOverview;
